import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  // ─── Admin / SuperAdmin login (unified) ────────────────────────────────
  const loginAdmin = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  // Keep legacy /auth/admin/login for AdminLoginPage compatibility
  const loginAdminLegacy = async (email, password) => {
    const { data } = await api.post('/auth/admin/login', { email, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const registerAdmin = async (formData) => {
    const { data } = await api.post('/auth/admin/register', formData);
    return data;
  };

  const verifyOTP = async (email, otp) => {
    const { data } = await api.post('/auth/admin/verify-otp', { email, otp });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const loginStudent = async (hallTicketNumber, password, examCode) => {
    const { data } = await api.post('/auth/student/login', { hallTicketNumber, password, examCode });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const verifyStudentOTP = async (email, otp) => {
    const { data } = await api.post('/auth/student/verify-otp', { email, otp });
    return data;
  };

  const resendStudentOTP = async (email) => {
    const { data } = await api.post('/auth/student/resend-otp', { email });
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // ─── Role helpers ──────────────────────────────────────────────────────
  const isSuperAdmin = () => user?.role === 'superadmin';
  const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
  const isStudent = () => user?.role === 'student';

  // ─── Permission helpers ────────────────────────────────────────────────
  // superadmin has all permissions ('all' string or full array)
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    if (user.permissions === 'all') return true;
    if (Array.isArray(user.permissions)) return user.permissions.includes(permission);
    return false;
  }, [user]);

  const canAddQuestions     = () => hasPermission('add_questions');
  const canEditQuestions    = () => hasPermission('edit_questions');
  const canDeleteQuestions  = () => hasPermission('delete_questions');
  const canApproveQuestions = () => hasPermission('approve_questions');
  const canManageStudents   = () => hasPermission('manage_students');
  const canViewReports      = () => hasPermission('view_reports');
  const canManageTests      = () => hasPermission('manage_tests');
  const canManageExamSettings = () => hasPermission('manage_exam_settings');

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      loginAdmin,
      loginAdminLegacy,
      loginStudent,
      registerAdmin,
      verifyOTP,
      verifyStudentOTP,
      resendStudentOTP,
      logout,
      isSuperAdmin,
      isAdmin,
      isStudent,
      hasPermission,
      canAddQuestions,
      canEditQuestions,
      canDeleteQuestions,
      canApproveQuestions,
      canManageStudents,
      canViewReports,
      canManageTests,
      canManageExamSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
};
